/**
 * Zunelo Client Factory & Exports
 *
 * Provides singletons for Jira, Slack, GitHub, Agents, Gmail, Workspace and Settings clients.
 * Production clients are live by default. Explicit demo mocks are selected with
 * the `demo` query parameter; Vitest keeps deterministic mocks for unit tests.
 */

import {
  IJiraClient,
  ISlackClient,
  IGitHubClient,
  IAgentsClient,
  IGmailClient,
  IWorkspaceClient,
  ISettingsClient,
  INotificationsClient,
  IOpenAiClient,
  IConversationsClient,
  IUnattendedInboxClient,
  IPeopleClient,
  ClientConfig,
} from './types';
import { MockJiraClient } from './mockJiraClient';
import { HttpJiraClient } from './httpJiraClient';
import { MockSlackClient } from './mockSlackClient';
import { HttpSlackClient } from './httpSlackClient';
import { MockGitHubClient } from './mockGitHubClient';
import { HttpGitHubClient } from './httpGitHubClient';
import { MockAgentsClient } from './mockAgentsClient';
import { MockGmailClient } from './mockGmailClient';
import { HttpGmailClient } from './httpGmailClient';
import { MockWorkspaceClient } from './mockWorkspaceClient';
import { HttpWorkspaceClient } from './httpWorkspaceClient';
import { MockSettingsClient } from './mockSettingsClient';
import { HttpSettingsClient } from './httpSettingsClient';
import { MockNotificationsClient } from './mockNotificationsClient';
import { HttpNotificationsClient } from './httpNotificationsClient';
import { HttpOpenAiClient } from './httpOpenAiClient';
import { HttpProviderOAuthClient } from './httpProviderOAuthClient';
import { MockConversationsClient } from './mockConversationsClient';
import { HttpConversationsClient } from './httpConversationsClient';
import { MockUnattendedInboxClient } from './mockUnattendedInboxClient';
import { HttpUnattendedInboxClient } from './httpUnattendedInboxClient';
import { HttpPeopleClient } from './httpPeopleClient';
import { MockPeopleClient } from './mockPeopleClient';
import { DEFAULT_CONFIG } from './mockClientUtils';
import { resolveApiBaseUrl } from '../../config/apiEndpoints';
import {
  setSourceCacheUidResolver,
  wrapAgentsClient,
  wrapGmailClient,
  wrapGitHubClient,
  wrapJiraClient,
  wrapSlackClient,
  wrapUnattendedInboxClient,
  wrapWorkspaceClient,
} from '../cache/wrapListClient';
import { resetDefaultSourceCacheForTests } from '../cache/sourceCache';

/**
 * Only the symbols actually consumed outside this module are re-exported
 * here (verified via a repo-wide usage grep) — a blanket `export *` across
 * ~20 client modules made every mock/HTTP class and internal helper part of
 * this module's public surface whether or not anything used it.
 */
export type { IWorkspaceClient } from './types';
export { MockJiraClient } from './mockJiraClient';
export { HttpJiraClient } from './httpJiraClient';
export { MockSlackClient } from './mockSlackClient';
export { MockGitHubClient } from './mockGitHubClient';
export { HttpGitHubClient } from './httpGitHubClient';
export { MockAgentsClient } from './mockAgentsClient';
export { MockGmailClient } from './mockGmailClient';
export { MockWorkspaceClient } from './mockWorkspaceClient';
export { HttpWorkspaceClient } from './httpWorkspaceClient';
export { MockSettingsClient } from './mockSettingsClient';
export { simulateNetwork } from './mockClientUtils';
export { resetMockConversations } from './mockConversationsClient';
export { migrateLocalWorkspaceToolsIfNeeded } from './migrateLocalWorkspaceTools';

/** Sources with a production BFF. */
export const LIVE_BFF_SOURCES = [
  'slack',
  'jira',
  'github',
  'gmail',
  'settings',
  'workspace',
  'conversations',
  'people',
] as const;

/** HTTP client stubs without BFF routes remain unavailable outside demo mode. */
export const MOCK_ONLY_SOURCES = ['agents'] as const;

export type LiveBffSource = (typeof LIVE_BFF_SOURCES)[number];
export type MockOnlySource = (typeof MOCK_ONLY_SOURCES)[number];
export type ClientSource = LiveBffSource | MockOnlySource | 'unattended';

export function sourceHasLiveBff(source: ClientSource): boolean {
  if ((MOCK_ONLY_SOURCES as readonly string[]).includes(source)) return false;
  if (source === 'unattended') return true;
  return (LIVE_BFF_SOURCES as readonly string[]).includes(source);
}

type IdTokenGetter = () => Promise<string | null | undefined> | string | null | undefined;

let getIdTokenForApi: IdTokenGetter | undefined;

/**
 * Re-instantiate every client singleton against `config`. Shared by
 * `setApiIdTokenGetter` (auth token changed — clients rebuilt so live HTTP
 * clients pick it up) and `configureClients` (latency / error simulation
 * changed) instead of each hand-rolling the same 10-assignment block.
 */
function rebuildClients(config: ClientConfig): void {
  jiraClient = createJiraClient(config);
  slackClient = createSlackClient(config);
  gitHubClient = createGitHubClient(config);
  agentsClient = createAgentsClient(config);
  gmailClient = createGmailClient(config);
  workspaceClient = createWorkspaceClient(config);
  settingsClient = createSettingsClient(config);
  openAiClient = new HttpOpenAiClient(liveHttpOptions());
  providerOAuthClient = new HttpProviderOAuthClient(liveHttpOptions());
  conversationsClient = createConversationsClient(config);
  peopleClient = createPeopleClient(config);
  unattendedInboxClient = createUnattendedInboxClient(config);
  notificationsClient = createNotificationsClient(config);
}

/** Wire Firebase (or test) ID token provider used by live HTTP clients. */
export function setApiIdTokenGetter(getter: IdTokenGetter | undefined): void {
  getIdTokenForApi = getter;
  rebuildClients(currentConfig);
}

/** Bind the authenticated uid used to namespace SPA source-cache keys. */
export function setSourceCacheUser(uid: string | undefined): void {
  setSourceCacheUidResolver(() => uid);
}

const DEMO_SOURCES = [...LIVE_BFF_SOURCES, 'unattended', ...MOCK_ONLY_SOURCES] as const;
export type DemoSource = (typeof DEMO_SOURCES)[number] | 'all';

export function parseDemoSources(search: string): DemoSource[] {
  const params = new URLSearchParams(search);
  const names = params
    .getAll('demo')
    .flatMap((value) => value.split(','))
    .map((name) => name.trim().toLowerCase())
    .filter(Boolean);
  if (names.length === 0) return [];
  if (names.includes('all')) return ['all'];
  return [
    ...new Set(
      names.filter((name): name is DemoSource =>
        (DEMO_SOURCES as readonly string[]).includes(name),
      ),
    ),
  ];
}

function readDemoSources(): Set<DemoSource> {
  if (typeof window === 'undefined') return new Set();
  return new Set(parseDemoSources(window.location.search));
}

export function isDemoSourceEnabled(source: ClientSource): boolean {
  if (import.meta.env.MODE === 'test') return true;
  const demoSources = readDemoSources();
  return demoSources.has('all') || demoSources.has(source);
}

export function getDemoSources(): DemoSource[] {
  const sources = [...readDemoSources()];
  return sources.includes('all') ? [...DEMO_SOURCES] : sources;
}

export function shouldUseMockSlack(): boolean {
  return isDemoSourceEnabled('slack');
}
export function shouldUseMockSettings(): boolean {
  return isDemoSourceEnabled('settings');
}
export function shouldUseMockJira(): boolean {
  return isDemoSourceEnabled('jira');
}
export function shouldUseMockGitHub(): boolean {
  return isDemoSourceEnabled('github');
}
export function shouldUseMockAgents(): boolean {
  return isDemoSourceEnabled('agents');
}
export function shouldUseMockGmail(): boolean {
  return isDemoSourceEnabled('gmail');
}
export function shouldUseMockWorkspace(): boolean {
  return isDemoSourceEnabled('workspace');
}
export function shouldUseMockConversations(): boolean {
  return isDemoSourceEnabled('conversations');
}

export function shouldUseMockPeople(): boolean {
  return isDemoSourceEnabled('people');
}

export function createPeopleClient(config: ClientConfig = DEFAULT_CONFIG): IPeopleClient {
  return shouldUseMockPeople()
    ? new MockPeopleClient(config)
    : new HttpPeopleClient(liveHttpOptions());
}

function liveHttpOptions(): { baseUrl: string; getIdToken: IdTokenGetter | undefined } {
  return {
    baseUrl: resolveApiBaseUrl(import.meta.env),
    getIdToken: getIdTokenForApi,
  };
}

export function createJiraClient(config: ClientConfig = DEFAULT_CONFIG): IJiraClient {
  const base = shouldUseMockJira()
    ? new MockJiraClient(config)
    : new HttpJiraClient(liveHttpOptions());
  return wrapJiraClient(base);
}

export function createSlackClient(config: ClientConfig = DEFAULT_CONFIG): ISlackClient {
  const base = shouldUseMockSlack()
    ? new MockSlackClient(config)
    : new HttpSlackClient(liveHttpOptions());
  return wrapSlackClient(base);
}

export function createGitHubClient(config: ClientConfig = DEFAULT_CONFIG): IGitHubClient {
  const base = shouldUseMockGitHub()
    ? new MockGitHubClient(config)
    : new HttpGitHubClient(liveHttpOptions());
  return wrapGitHubClient(base);
}

/** Agents has no BFF; it is empty unless explicitly enabled in demo mode. */
export function createAgentsClient(config: ClientConfig = DEFAULT_CONFIG): IAgentsClient {
  return wrapAgentsClient(new MockAgentsClient(config, shouldUseMockAgents()));
}

export function createGmailClient(config: ClientConfig = DEFAULT_CONFIG): IGmailClient {
  const base = shouldUseMockGmail()
    ? new MockGmailClient(config, true)
    : new HttpGmailClient(liveHttpOptions());
  return wrapGmailClient(base);
}

export function createWorkspaceClient(config: ClientConfig = DEFAULT_CONFIG): IWorkspaceClient {
  const base = shouldUseMockWorkspace()
    ? new MockWorkspaceClient(config)
    : new HttpWorkspaceClient(liveHttpOptions());
  return wrapWorkspaceClient(base);
}

export function createSettingsClient(config: ClientConfig = DEFAULT_CONFIG): ISettingsClient {
  if (shouldUseMockSettings()) return new MockSettingsClient(config);
  return new HttpSettingsClient(liveHttpOptions());
}

/** Push registration follows the Settings mock switch (no BFF in demo/Vitest). */
export function createNotificationsClient(
  config: ClientConfig = DEFAULT_CONFIG,
): INotificationsClient {
  if (shouldUseMockSettings()) return new MockNotificationsClient(config);
  return new HttpNotificationsClient(liveHttpOptions());
}

export function createConversationsClient(
  config: ClientConfig = DEFAULT_CONFIG,
): IConversationsClient {
  if (shouldUseMockConversations()) return new MockConversationsClient(config);
  return new HttpConversationsClient(liveHttpOptions());
}

/** Live unattended-inbox outside tests; demo mode uses its deterministic mock. */
export function shouldUseMockUnattendedInbox(): boolean {
  return isDemoSourceEnabled('unattended');
}

export function createUnattendedInboxClient(
  config: ClientConfig = DEFAULT_CONFIG,
): IUnattendedInboxClient {
  const base = shouldUseMockUnattendedInbox()
    ? new MockUnattendedInboxClient(config)
    : new HttpUnattendedInboxClient(liveHttpOptions());
  return wrapUnattendedInboxClient(base);
}

// Configurable client instances
let currentConfig: ClientConfig = { ...DEFAULT_CONFIG };

export let jiraClient: IJiraClient = createJiraClient(currentConfig);
export let slackClient: ISlackClient = createSlackClient(currentConfig);
export let gitHubClient: IGitHubClient = createGitHubClient(currentConfig);
export let agentsClient: IAgentsClient = createAgentsClient(currentConfig);
export let gmailClient: IGmailClient = createGmailClient(currentConfig);
export let workspaceClient: IWorkspaceClient = createWorkspaceClient(currentConfig);
export let settingsClient: ISettingsClient = createSettingsClient(currentConfig);
export let openAiClient: IOpenAiClient = new HttpOpenAiClient(liveHttpOptions());
export let providerOAuthClient = new HttpProviderOAuthClient(liveHttpOptions());
export let conversationsClient: IConversationsClient = createConversationsClient(currentConfig);
export let peopleClient: IPeopleClient = createPeopleClient(currentConfig);
export let unattendedInboxClient: IUnattendedInboxClient =
  createUnattendedInboxClient(currentConfig);
export let notificationsClient: INotificationsClient = createNotificationsClient(currentConfig);

/**
 * Configure or re-instantiate clients with custom latency / error simulation.
 */
export function configureClients(config: Partial<ClientConfig>): void {
  currentConfig = { ...currentConfig, ...config };
  resetDefaultSourceCacheForTests();
  rebuildClients(currentConfig);
}

export type WorkspaceDataMode = 'live' | 'hybrid' | 'demo';

export interface DataModeInfo {
  mode: WorkspaceDataMode;
  label: string;
  badgeText: string;
  slackLive: boolean;
  settingsLive: boolean;
  details: string;
}

/**
 * Returns summary information about the current data modes in use.
 * Provides transparent visibility in the UI regarding live vs mock/demo data sources.
 */
export function getDataModeInfo(overrides?: {
  slackMock?: boolean;
  settingsMock?: boolean;
  jiraMock?: boolean;
  gitHubMock?: boolean;
}): DataModeInfo {
  const isSlackMock = overrides?.slackMock ?? shouldUseMockSlack();
  const isSettingsMock = overrides?.settingsMock ?? shouldUseMockSettings();
  const isJiraMock = overrides?.jiraMock ?? shouldUseMockJira();
  const isGitHubMock = overrides?.gitHubMock ?? shouldUseMockGitHub();

  const isLiveSlack = !isSlackMock;
  const isLiveJira = !isJiraMock;
  const isLiveGitHub = !isGitHubMock;
  const demoSources = getDemoSources().filter((source) => source !== 'all');

  const allLive = isLiveSlack && isLiveJira && isLiveGitHub;
  const anyLive = isLiveSlack || isLiveJira || isLiveGitHub;

  if (allLive) {
    return {
      mode: 'live',
      label: 'Live',
      badgeText: 'Workspace Live',
      slackLive: true,
      settingsLive: !isSettingsMock,
      details: 'Todos los servicios sincronizados en tiempo real con sus respectivos backends.',
    };
  }

  if (anyLive) {
    const liveSources = [
      isLiveSlack ? 'Slack' : null,
      isLiveJira ? 'Jira' : null,
      isLiveGitHub ? 'GitHub' : null,
    ].filter(Boolean);

    return {
      mode: 'hybrid',
      label: 'Híbrido',
      badgeText:
        demoSources.length > 0
          ? `${liveSources.join(' & ')} Live · Demo: ${demoSources.join(', ')}`
          : `${liveSources.join(' & ')} Live · Workspace Demo`,
      slackLive: isLiveSlack,
      settingsLive: !isSettingsMock,
      details:
        demoSources.length > 0
          ? `${liveSources.join(', ')} sincronizado en tiempo real vía API. Demo explícito para: ${demoSources.join(', ')}.`
          : `${liveSources.join(', ')} sincronizado en tiempo real vía API. Los demás servicios operan con datos interactivos locales.`,
    };
  }

  return {
    mode: 'demo',
    label: 'Demo',
    badgeText: 'Workspace Demo',
    slackLive: false,
    settingsLive: !isSettingsMock,
    details: 'Todos los servicios operan con datos enriquecidos interactivos locales.',
  };
}
